import { Form } from "@unform/web";
import {
  ClipboardCheck,
  FileText,
  PlusCircle,
  Clock,
  NotebookPen,
  MessageSquare,
  RotateCcw,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import FormLoading from "~/components/RegisterForm/FormLoading";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import Textarea from "~/components/RegisterForm/Textarea";
import TdRadioInput from "~/components/RegisterForm/TdRadioInput";
import PDFViewer from "~/components/PDFViewer";
import Comments from "./Comments";
import { FullGridContext } from "../..";
import { AlertContext } from "~/App";
import { format, parseISO } from "date-fns";
import { Scope, useField } from "@unform/core";
import NewDatepicker from "~/pages/Financial/Outbounds/Payees/ModalFilters/NewDatepicker";

export const InputContext = createContext({});
export const AttendanceInputContext = createContext({});

function ExcuseDateField({
  name,
  title,
  required = false,
  grow = false,
  defaultValue = "",
  InputContext = null,
  onValueChange = () => {},
}) {
  const { fieldName, registerField } = useField(name);
  const { setSuccessfullyUpdated } = useContext(InputContext);
  const [value, setValue] = useState(defaultValue || "");
  const fieldRef = useRef({ value: defaultValue || "" });

  useEffect(() => {
    const initialValue = defaultValue || "";
    fieldRef.current.value = initialValue;
    setValue(initialValue);
  }, [defaultValue]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: fieldRef.current,
      path: "value",
      setValue: (ref, nextValue) => {
        const resolved = nextValue || "";
        ref.value = resolved;
        setValue(resolved);
      },
      clearValue: (ref) => {
        ref.value = "";
        setValue("");
      },
    });
  }, [fieldName, registerField]);

  function handleChange(nextValue) {
    fieldRef.current.value = nextValue || "";
    setValue(nextValue || "");
    setSuccessfullyUpdated(false);
    onValueChange(nextValue || "");
  }

  return (
    <div className={`flex flex-col justify-center items-start ${grow ? "grow" : ""}`}>
      <div className="px-2 text-xs flex flex-row justify-between items-center">
        {title} {required && <span className="text-red-600">*</span>}
      </div>
      <div className="w-full border border-gray-300 rounded-xs px-2 py-1 text-sm">
        <NewDatepicker
          value={value}
          onChange={handleChange}
          className="w-full"
          placeholder="MM/DD/YYYY"
        />
      </div>
    </div>
  );
}

function isImageFile(file) {
  const name = file?.name || file?.url || "";
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(name);
}

const shiftOptions = [
  { value: "Morning", label: "Morning" },
  { value: "Afternoon", label: "Afternoon" },
  { value: "Full Day", label: "Full Day" },
];

const FIXED_STUDENT_ID = "0650968a-8b03-4b4b-8734-f9df270a8cfa";

const formatDate = (d) => {
  if (!d) return "-";
  try {
    const normalized = d.length === 10 ? d + "T00:00:00" : d;
    return format(new Date(normalized), "MM/dd/yyyy");
  } catch {
    return d;
  }
};

const formatDateTime = (d) => {
  if (!d) return "-";
  try {
    return format(new Date(d), "MM/dd/yyyy @ HH:mm");
  } catch {
    return d;
  }
};

function StatusBadge({ status }) {
  const map = {
    Approved: "bg-green-100 text-green-700",
    Denied: "bg-red-100 text-red-700",
    "In Progress": "bg-blue-100 text-blue-700",
    "More Information": "bg-yellow-100 text-yellow-700",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map[status] ?? "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

function TimelineEntry({ log }) {
  const actionColor = {
    Submitted: "bg-blue-500",
    Approved: "bg-green-500",
    Denied: "bg-red-500",
    Escalated: "bg-purple-500",
    "More Information": "bg-yellow-500",
    Reviewed: "bg-teal-500",
  };
  const color = actionColor[log.action] ?? "bg-gray-400";

  return (
    <div className="flex gap-3 items-start">
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${color}`} />
        <div className="w-px flex-1 bg-gray-200 mt-1" />
      </div>
      <div className="pb-4 flex-1">
        <p className="text-xs font-semibold text-gray-700">
          {log.action}
          {log.step ? ` — ${log.step}` : ""}
        </p>
        {log.notes && <p className="text-xs text-gray-500 mt-0.5">{log.notes}</p>}
        <p className="text-xs text-gray-400 mt-0.5">{formatDateTime(log.created_at)}</p>
      </div>
    </div>
  );
}

function AttendanceTable({ attendances, Ctx, formRef, readOnly = false }) {
  const [openNote, setOpenNote] = useState(null);

  if (!attendances || attendances.length === 0) {
    return <p className="text-xs text-gray-400 py-4">No attendance records for this period.</p>;
  }

  const rows = attendances.filter((a) => a.status !== "T");

  return (
    <Form ref={formRef} onSubmit={() => {}} className="w-full">
      <table className="text-sm text-center" style={{ tableLayout: "fixed", width: "100%" }}>
        <colgroup>
          <col style={{ width: "18%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "2%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "2%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
        </colgroup>
        <thead>
          <tr>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
            <th colSpan={3}>1st Check</th>
            <th></th>
            <th colSpan={3}>2nd Check</th>
            <th></th>
            <th colSpan={2}>Vac & ME</th>
          </tr>
          <tr className="bg-gray-100">
            <th className="px-2 h-8 text-left">Group</th>
            <th className="px-2 h-8 text-left">Date</th>
            <th className="px-2 h-8 text-left">Shift</th>
            <th className="px-2 h-8 text-left">Notes</th>
            <th className="bg-yellow-500">L/E</th>
            <th className="bg-green-500">P</th>
            <th className="bg-red-500">A</th>
            <th></th>
            <th className="bg-yellow-500">L/E</th>
            <th className="bg-green-500">P</th>
            <th className="bg-red-500">A</th>
            <th></th>
            <th className="bg-amber-700">V</th>
            <th className="bg-emerald-500">S</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((attendance, index) => {
            const {
              first_check = null,
              second_check = null,
              vacation_id = null,
              medical_excuse_id = null,
              id: attendanceId = null,
            } = attendance;

            return (
              <Scope path={`attendances.${index}`} key={index}>
                <tr className="text-center even:bg-gray-50">
                  <td className="px-2 h-8 text-left">
                    {attendance.studentgroupclasses?.studentgroup?.name ?? "-"}
                  </td>
                  <td className="px-2 h-8 text-left">
                    <Input
                      type="hidden"
                      name="id"
                      defaultValue={attendanceId}
                      InputContext={Ctx}
                    />
                    {attendance.studentgroupclasses?.date
                      ? format(parseISO(attendance.studentgroupclasses.date), "MM/dd")
                      : "-"}
                  </td>
                  <td className="px-2 h-8 text-left">
                    {attendance.studentgroupclasses?.shift ?? "-"}
                  </td>
                  <td className="px-2 h-8 text-left">
                    <NotebookPen
                      size={14}
                      className={`cursor-pointer ${attendance.dso_note ? "text-mila_orange" : ""}`}
                      onClick={() => setOpenNote(openNote === attendanceId ? null : attendanceId)}
                    />
                  </td>
                  <TdRadioInput
                    name={`first_check_${attendanceId}`}
                    options={["Late", "Present", "Absent"]}
                    readOnly={readOnly || !!(medical_excuse_id || vacation_id)}
                    InputContext={Ctx}
                    defaultValue={!first_check || medical_excuse_id ? "Absent" : first_check}
                  />
                  <td></td>
                  <TdRadioInput
                    name={`second_check_${attendanceId}`}
                    options={["Late", "Present", "Absent"]}
                    readOnly={readOnly || !!(medical_excuse_id || vacation_id)}
                    InputContext={Ctx}
                    defaultValue={!second_check || medical_excuse_id ? "Absent" : second_check}
                  />
                  <td></td>
                  <TdRadioInput
                    name={`vacation_${attendanceId}`}
                    readOnly
                    options={["Vacation"]}
                    InputContext={Ctx}
                    defaultValue={vacation_id ? "Vacation" : null}
                  />
                  <TdRadioInput
                    name={`medical_excuse_${attendanceId}`}
                    readOnly
                    options={["Medical Excuse"]}
                    InputContext={Ctx}
                    defaultValue={medical_excuse_id ? "Medical Excuse" : null}
                  />
                </tr>
                <tr className={openNote === attendanceId ? "" : "hidden"}>
                  <td colSpan={14}>
                    <Input
                      type="text"
                      name={`dso_note_${attendanceId}`}
                      grow
                      className="mb-4 shadow-xs"
                      InputContext={Ctx}
                      defaultValue={attendance.dso_note}
                    />
                  </td>
                </tr>
              </Scope>
            );
          })}
        </tbody>
      </table>
    </Form>
  );
}

export default function PagePreview({ access, id, defaultFormType = "preview" }) {
  const { alertBox } = useContext(AlertContext);
  const { handleOpened, successfullyUpdated, setSuccessfullyUpdated } = useContext(FullGridContext);

  const [pageData, setPageData] = useState(null);
  const [formType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("request");
  const [submitting, setSubmitting] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [openedFileIndex, setOpenedFileIndex] = useState(0);
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [editedDates, setEditedDates] = useState({ start_date: null, end_date: null });

  const generalForm = useRef();
  const attendanceForm = useRef();
  const isNew = id === "new";

  useEffect(() => {
    document.getElementById("scrollPage")?.scrollTo(0, 0);
  }, [activeMenu]);

  async function getPageData() {
    try {
      const { data } = await api.get(`/medical_excuse_request/${id}`);
      setPageData({ ...data, loaded: true });
      setEditedDates({ start_date: null, end_date: null });
      // Default: all attached files selected to be copied to the official MedicalExcuse on approval.
      setSelectedFileIds(
        new Set(
          (data.files || [])
            .map((f) => f.file?.id)
            .filter((fileId) => fileId != null)
        )
      );
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    }
  }

  useEffect(() => {
    if (!isNew) {
      getPageData();
    } else {
      setPageData({ loaded: true });
    }
  }, []);

  async function handleSaveDates() {
    const data = generalForm.current?.getData() ?? {};
    const { start_date, end_date } = data;

    if (!start_date) {
      toast("Start date is required.", { type: "error", autoClose: 3000 });
      return;
    }
    if (!end_date) {
      toast("End date is required.", { type: "error", autoClose: 3000 });
      return;
    }
    if (end_date < start_date) {
      toast("End date must be after start date.", { type: "error", autoClose: 3000 });
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/medical_excuse_request/${id}/dates`, { start_date, end_date });
      toast("Dates updated!", { autoClose: 1500 });
      setSuccessfullyUpdated(true);
      await getPageData();
    } catch (err) {
      const msg = err?.response?.data?.error || "Error updating dates.";
      toast(msg, { type: "error", autoClose: 3000 });
    } finally {
      setSubmitting(false);
    }
  }

  const viewerRole = pageData?.viewer_role ?? null;
  const isAcademicViewer = viewerRole === "Academic";
  const isDSOViewer = viewerRole === "DSO" || viewerRole === "Holding";

  const canReviewDSO =
    !isNew &&
    pageData?.status === "In Progress" &&
    pageData?.current_step === "DSO" &&
    isDSOViewer;

  const canReviewAcademic =
    !isNew &&
    pageData?.status === "In Progress" &&
    pageData?.current_step === "Academic" &&
    (isAcademicViewer || viewerRole === "Holding");

  const canReview = canReviewDSO || canReviewAcademic;
  const showAttendanceInReview = !isNew && isAcademicViewer;

  const toIsoDay = (d) => (d ? String(d).slice(0, 10) : "");
  const datesChanged =
    (editedDates.start_date !== null &&
      editedDates.start_date !== toIsoDay(pageData?.start_date)) ||
    (editedDates.end_date !== null &&
      editedDates.end_date !== toIsoDay(pageData?.end_date));

  function toggleFileSelected(fileId) {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }

  function collectAttendances() {
    if (!attendanceForm.current || !pageData?.attendances) return [];
    const formData = attendanceForm.current.getData();
    const rows = pageData.attendances.filter((a) => a.status !== "T");
    return rows.map((attendance, index) => {
      const { id: attendanceId } = attendance;
      const entry = formData?.attendances?.[index] ?? {};
      return {
        id: attendanceId,
        first_check: entry[`first_check_${attendanceId}`] ?? attendance.first_check,
        second_check: entry[`second_check_${attendanceId}`] ?? attendance.second_check,
        dso_note: entry[`dso_note_${attendanceId}`] ?? attendance.dso_note ?? null,
      };
    });
  }

  async function handleCreateSubmit(data) {
    if (!data.start_date) {
      toast("Start date is required.", { type: "error", autoClose: 3000 });
      return;
    }
    if (!data.end_date) {
      toast("End date is required.", { type: "error", autoClose: 3000 });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("student_id", FIXED_STUDENT_ID);
      formData.append("start_date", data.start_date);
      formData.append("end_date", data.end_date);
      if (data.student_notes) formData.append("student_notes", data.student_notes);
      if (data.shift) formData.append("shift", data.shift);
      for (const file of selectedFiles) {
        formData.append("files", file);
      }

      await api.post("/medical_excuse_request", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast("Absence excuse submitted!", { autoClose: 1500 });
      setSuccessfullyUpdated(true);
      handleOpened(null);
    } catch (err) {
      const msg = err?.response?.data?.error || "Error submitting request.";
      toast(msg, { type: "error", autoClose: 3000 });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGeneralFormSubmit(data) {
    if (isNew) {
      handleCreateSubmit(data);
      return;
    }

    if (!canReview) return;

    const action = selectedStatus?.value;

    if (!action) {
      toast("Select an action.", { type: "error", autoClose: 3000 });
      return;
    }

    if (action === "Denied" && !data.denied_reason?.trim()) {
      toast("Denied reason is required.", { type: "error", autoClose: 3000 });
      return;
    }

    const isEscalate = action === "Escalate to Academic";

    alertBox({
      title: "Attention!",
      descriptionHTML: `Are you sure you want to <strong>${action}</strong> this request? This action cannot be undone.`,
      buttons: [
        { title: "No", class: "cancel" },
        {
          title: "Yes",
          onPress: async () => {
            setSubmitting(true);
            try {
              if (isEscalate) {
                await api.put(`/medical_excuse_request/${id}/escalate`, {
                  notes: data.escalate_notes || null,
                });
              } else if (canReviewAcademic) {
                const payload = {
                  status: action,
                  review_notes: action === "Denied" ? null : (data.review_notes || null),
                  denied_reason: action === "Denied" ? data.denied_reason : null,
                  internal_notes: data.internal_notes || null,
                  staff_id: null,
                  attendances: collectAttendances(),
                };
                await api.put(`/medical_excuse_request/${id}/review`, payload);
              } else {
                const payload = {
                  status: action,
                  review_notes: action === "Denied" ? null : (data.review_notes || null),
                  denied_reason: action === "Denied" ? data.denied_reason : null,
                  internal_notes: data.internal_notes || null,
                  // DSO approval: copy only the selected files to the official MedicalExcuse.
                  // Backend ignores this on Denied / More Information.
                  selected_file_ids: Array.from(selectedFileIds),
                };
                await api.put(`/medical_excuse_request/${id}/review`, payload);
              }
              toast("Done!", { autoClose: 1500 });
              setSuccessfullyUpdated(true);
              handleOpened(null);
            } catch (err) {
              const msg = err?.response?.data?.error || "Error submitting.";
              toast(msg, { type: "error", autoClose: 3000 });
            } finally {
              setSubmitting(false);
            }
          },
        },
      ],
    });
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarded!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleRollback() {
    const isAcademic = isAcademicViewer;
    const title = isAcademic
      ? "Attention: This action marks absences"
      : "Revert Medical Excuse";
    const descriptionHTML = isAcademic
      ? "Reverting this request will automatically mark <strong>Absent</strong> in all attendances linked to this Medical Excuse.<br/><br/>" +
        "The attendances pointed by this request will return as absent and the request will go back to the Academic queue as <strong>Pending</strong>.<br/><br/>" +
        "Please review the attendances right after reverting and adjust what is necessary before reprocessing.<br/><br/>" +
        "Do you want to continue?"
      : "This will revert the Medical Excuse approval, cancelling the medical certificate and its files.<br/><br/>" +
        "The request will return to the DSO queue as <strong>In Progress</strong>.<br/><br/>" +
        "Do you want to continue?";
    const confirmLabel = isAcademic ? "Revert and mark absent" : "Revert Medical Excuse";

    alertBox({
      title,
      descriptionHTML,
      buttons: [
        { title: "Cancel", class: "cancel" },
        {
          title: confirmLabel,
          onPress: async () => {
            setSubmitting(true);
            try {
              await api.put(`/medical_excuse_request/${id}/rollback`);
              toast(isAcademic ? "Rollback completed! Please review the attendances." : "Medical Excuse reverted successfully.", { autoClose: 3000 });
              handleOpened(null);
              setTimeout(() => handleOpened(id), 50);
            } catch (err) {
              const msg = err?.response?.data?.error || "Error performing rollback.";
              toast(msg, { type: "error", autoClose: 3000 });
            } finally {
              setSubmitting(false);
            }
          },
        },
      ],
    });
  }

  const menuItems = isNew
    ? []
    : [
        { name: "request", label: "Request", icon: <FileText size={16} /> },
        { name: "review", label: "Review", icon: <ClipboardCheck size={16} /> },
        { name: "timeline", label: "Timeline", icon: <Clock size={16} /> },
        { name: "internal_notes", label: "Internal Notes", icon: <MessageSquare size={16} /> },
      ];

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          {!isNew && (
            <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
              {menuItems.map((m) => (
                <RegisterFormMenu
                  key={m.name}
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name={m.name}
                >
                  {m.icon} {m.label}
                </RegisterFormMenu>
              ))}
            </div>
          )}

          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <InputContext.Provider
              value={{
                id,
                generalForm,
                setSuccessfullyUpdated,
                fullscreen,
                setFullscreen,
                successfullyUpdated,
                handleCloseForm,
                handleInactivate: () => null,
                canceled: false,
              }}
            >
              <div
                id="scrollPage"
                className="flex flex-col items-start justify-start text-sm overflow-y-scroll overflow-x-hidden h-full"
              >
                <Form ref={generalForm} onSubmit={handleGeneralFormSubmit} className="w-full">
                  {pageData.loaded ? (
                    <>
                      <FormHeader
                        access={{
                          ...access,
                          edit: canReview,
                          create: isNew,
                        }}
                        title={
                          isNew
                            ? "New Absence Excuse"
                            : `Absence Excuse ${pageData.token ? `— ${pageData.token}` : ""} — ${pageData.student?.name ?? ""} ${pageData.student?.last_name ?? ""}`
                        }
                        registry={{}}
                        InputContext={InputContext}
                        createText="Submit Request"
                        createIcon={<PlusCircle size={16} />}
                        otherButtons={[]}
                      />

                      {isNew && (
                        <InputLineGroup title="request" activeMenu={true}>
                          <InputLine title="Period">
                            <Input name="start_date" title="Start Date" type="date" grow required InputContext={InputContext} />
                            <Input name="end_date" title="End Date" type="date" grow required InputContext={InputContext} />
                          </InputLine>

                          <InputLine title="Shift">
                            <SelectPopover name="shift" title="Shift" grow isSearchable options={shiftOptions} InputContext={InputContext} />
                          </InputLine>

                          <InputLine title="Student Notes">
                            <Textarea name="student_notes" title="Student Notes" grow InputContext={InputContext} rows={5} />
                          </InputLine>

                          <InputLine title="Files">
                            <div className="flex flex-col gap-2 py-2 w-full">
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,image/*"
                                multiple
                                className="text-xs"
                                onChange={(e) => {
                                  const incoming = Array.from(e.target.files);
                                  setSelectedFiles((prev) => {
                                    const existing = new Set(prev.map((f) => f.name));
                                    return [...prev, ...incoming.filter((f) => !existing.has(f.name))];
                                  });
                                  e.target.value = "";
                                }}
                              />
                              {selectedFiles.length > 0 && (
                                <ul className="text-xs text-gray-600 list-disc ml-4 flex flex-col gap-1">
                                  {selectedFiles.map((f, i) => (
                                    <li key={i} className="flex items-center gap-2">
                                      <span>{f.name}</span>
                                      <button
                                        type="button"
                                        onClick={() => setSelectedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                                        className="text-red-500 hover:text-red-700 text-xs font-bold"
                                      >
                                        ✕
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </InputLine>
                        </InputLineGroup>
                      )}

                      {!isNew && (
                        <InputLineGroup title="request" activeMenu={activeMenu === "request"}>
                          <div className="w-full flex flex-row justify-between items-start gap-4">
                            <div className="flex-1 flex flex-col justify-start items-start">
                              {pageData.token && (
                                <InputLine title="Token">
                                  <div className="py-3 px-1">
                                    <span className="font-mono text-sm font-semibold bg-gray-100 px-3 py-1 rounded-md border border-gray-300 tracking-widest">
                                      {pageData.token}
                                    </span>
                                  </div>
                                </InputLine>
                              )}

                              <InputLine title="Student">
                                <Input name="_student_name" title="Name" grow readOnly defaultValue={`${pageData.student?.name ?? ""} ${pageData.student?.last_name ?? ""}`} InputContext={InputContext} />
                                <Input name="_registration" title="Registration #" shrink readOnly defaultValue={pageData.student?.registration_number ?? ""} InputContext={InputContext} />
                                <Input name="_email" title="Email" grow readOnly defaultValue={pageData.student?.email ?? ""} InputContext={InputContext} />
                              </InputLine>

                              <InputLine title="Period">
                                <ExcuseDateField name="start_date" title="Start Date" grow defaultValue={pageData.start_date} InputContext={InputContext} onValueChange={(v) => setEditedDates((prev) => ({ ...prev, start_date: v }))} />
                                <ExcuseDateField name="end_date" title="End Date" grow defaultValue={pageData.end_date} InputContext={InputContext} onValueChange={(v) => setEditedDates((prev) => ({ ...prev, end_date: v }))} />
                                <Input name="_submitted" title="Submitted on" grow readOnly defaultValue={formatDateTime(pageData.created_at)} InputContext={InputContext} />
                              </InputLine>

                              {datesChanged && (
                                <div className="flex justify-end w-full px-1 pb-2">
                                  <button
                                    type="button"
                                    onClick={handleSaveDates}
                                    disabled={submitting}
                                    className="bg-mila_orange text-white text-xs rounded-md px-4 py-2 h-8 disabled:opacity-50"
                                  >
                                    {submitting ? "Saving..." : "Save Changes"}
                                  </button>
                                </div>
                              )}

                              {pageData.shift && (
                                <InputLine title="Shift">
                                  <Input name="_shift" title="Shift" grow readOnly defaultValue={pageData.shift} InputContext={InputContext} />
                                </InputLine>
                              )}

                              <InputLine title="Status">
                                <div className="py-3 px-1 flex items-center gap-3">
                                  <StatusBadge status={pageData.status} />
                                  {pageData.current_step && (
                                    <span className="text-xs text-gray-500">Step: <strong>{pageData.current_step}</strong></span>
                                  )}
                                </div>
                              </InputLine>

                              {pageData.student_notes && (
                                <InputLine title="Student Notes">
                                  <Textarea name="_student_notes" title="Student Notes" grow readOnly defaultValue={pageData.student_notes} InputContext={InputContext} rows={4} />
                                </InputLine>
                              )}
                            </div>

                            {pageData.files && pageData.files.length > 0 && (
                              <div className="flex-1 flex flex-col">
                                <InputLine title="Attached Files">
                                  <div className="w-full flex flex-col gap-1 py-2">
                                    {canReviewDSO && (
                                      <span className="text-xs text-gray-500">
                                        Select which files will be copied to the official Medical Excuse on approval.
                                      </span>
                                    )}
                                    <div className="w-full flex flex-row flex-wrap items-center gap-2">
                                      {pageData.files.map((f, i) => {
                                        const fileId = f.file?.id;
                                        const isChecked =
                                          fileId != null && selectedFileIds.has(fileId);
                                        return (
                                          <div
                                            key={i}
                                            className="flex flex-row items-center gap-1"
                                          >
                                            {canReviewDSO && fileId != null && (
                                              <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => toggleFileSelected(fileId)}
                                                className="cursor-pointer"
                                                title="Copy this file to the official Medical Excuse"
                                              />
                                            )}
                                            <button
                                              type="button"
                                              onClick={() => setOpenedFileIndex(i)}
                                              className={`${
                                                openedFileIndex === i
                                                  ? "bg-primary text-white"
                                                  : "bg-secondary text-primary"
                                              } rounded-md py-2 px-4 h-6 flex flex-row items-center justify-center text-xs gap-1`}
                                            >
                                              {`File ${i + 1}`}
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </InputLine>

                                {pageData.files.map((f, i) => {
                                  if (openedFileIndex !== i) return null;
                                  const url = f.file?.url;
                                  if (!url) return null;
                                  return (
                                    <div
                                      key={`file-${i}`}
                                      className="w-full overflow-y-scroll h-[940px] border rounded-md"
                                    >
                                      {isImageFile(f.file) ? (
                                        <img
                                          src={url}
                                          alt={f.file?.name ?? `File ${i + 1}`}
                                          className="w-full h-auto object-contain"
                                        />
                                      ) : (
                                        <PDFViewer
                                          download={true}
                                          file={{ url }}
                                          height={900}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </InputLineGroup>
                      )}

                      {!isNew && (
                        <InputLineGroup title="review" activeMenu={activeMenu === "review"}>
                          {canReviewDSO && (
                            <>
                              <InputLine title="Action">
                                <SelectPopover
                                  name="status"
                                  title="Select an action"
                                  required
                                  grow
                                  isSearchable
                                  options={[
                                    { value: "Approved", label: "Approved" },
                                    { value: "Denied", label: "Denied" },
                                    { value: "More Information", label: "More Information" },
                                    { value: "Escalate to Academic", label: "Escalate to Academic" },
                                  ]}
                                  onChange={(val) => setSelectedStatus(val)}
                                  InputContext={InputContext}
                                />
                              </InputLine>

                              {(selectedStatus?.value === "Approved" || selectedStatus?.value === "More Information") && (
                                <InputLine title="Review Notes">
                                  <Textarea name="review_notes" title="Review Notes — sent to student (optional)" grow InputContext={InputContext} rows={4} />
                                </InputLine>
                              )}

                              {selectedStatus?.value === "Denied" && (
                                <InputLine title="Denied Reason">
                                  <Textarea name="denied_reason" title="Denied Reason" grow required InputContext={InputContext} rows={4} />
                                </InputLine>
                              )}

                              {selectedStatus?.value === "Escalate to Academic" && (
                                <InputLine title="Notes">
                                  <Textarea name="escalate_notes" title="Notes (optional)" grow InputContext={InputContext} rows={4} />
                                </InputLine>
                              )}

                              <InputLine title="Internal Notes">
                                <div className="flex flex-col gap-1 w-full py-1">
                                  <span className="text-xs text-gray-400">Internal only — not sent to the student</span>
                                  <Textarea name="internal_notes" title="Internal Notes (optional)" grow InputContext={InputContext} rows={3} />
                                </div>
                              </InputLine>
                            </>
                          )}

                          {pageData.status === "In Progress" && pageData.current_step === "Academic" && !canReviewAcademic && (
                            <InputLine title="Status">
                              <div className="py-3 flex flex-col gap-1">
                                <StatusBadge status="In Progress" />
                                <p className="text-xs text-gray-500 mt-1">Waiting for Academic department review.</p>
                              </div>
                            </InputLine>
                          )}

                          {canReviewAcademic && (
                            <>
                              <InputLine title="Action">
                                <SelectPopover
                                  name="status"
                                  title="Select an action"
                                  required
                                  grow
                                  isSearchable
                                  options={[
                                    { value: "Approved", label: "Approved" },
                                    { value: "Denied", label: "Denied" },
                                    { value: "More Information", label: "More Information" },
                                  ]}
                                  onChange={(val) => setSelectedStatus(val)}
                                  InputContext={InputContext}
                                />
                              </InputLine>

                              {(selectedStatus?.value === "Approved" || selectedStatus?.value === "More Information") && (
                                <InputLine title="Review Notes">
                                  <Textarea name="review_notes" title="Review Notes — sent to student (optional)" grow InputContext={InputContext} rows={4} />
                                </InputLine>
                              )}

                              {selectedStatus?.value === "Denied" && (
                                <InputLine title="Denied Reason">
                                  <Textarea name="denied_reason" title="Denied Reason" grow required InputContext={InputContext} rows={4} />
                                </InputLine>
                              )}

                              <InputLine title="Internal Notes">
                                <div className="flex flex-col gap-1 w-full py-1">
                                  <span className="text-xs text-gray-400">Internal only — not sent to the student</span>
                                  <Textarea name="internal_notes" title="Internal Notes (optional)" grow InputContext={InputContext} rows={3} />
                                </div>
                              </InputLine>

                              {selectedStatus?.value === "Approved" && (
                                <InputLine title="Attendance Adjustments">
                                  <div className="w-full py-2">
                                    <AttendanceInputContext.Provider
                                      value={{
                                        id,
                                        generalForm: attendanceForm,
                                        setSuccessfullyUpdated: () => {},
                                        fullscreen,
                                        setFullscreen,
                                        successfullyUpdated: true,
                                        handleCloseForm,
                                        handleInactivate: () => null,
                                        canceled: false,
                                      }}
                                    >
                                      <AttendanceTable
                                        attendances={pageData.attendances}
                                        Ctx={AttendanceInputContext}
                                        formRef={attendanceForm}
                                        readOnly={false}
                                      />
                                    </AttendanceInputContext.Provider>
                                  </div>
                                </InputLine>
                              )}
                            </>
                          )}

                          {pageData.status === "More Information" && (
                            <InputLine title="Status">
                              <div className="py-3 flex flex-col gap-1">
                                <StatusBadge status="More Information" />
                                <p className="text-xs text-gray-500 mt-1">Waiting for the student to resubmit additional information.</p>
                                {pageData.review_notes && (
                                  <p className="text-xs text-gray-600 mt-2 italic">"{pageData.review_notes}"</p>
                                )}
                              </div>
                            </InputLine>
                          )}

                          {pageData.status === "Approved" && (
                            <>
                              <InputLine title="Status">
                                <div className="py-3 flex flex-col gap-2">
                                  <StatusBadge status="Approved" />
                                  {pageData.review_notes && (
                                    <p className="text-xs text-gray-600 mt-1">{pageData.review_notes}</p>
                                  )}
                                </div>
                              </InputLine>
                              {showAttendanceInReview && (
                                <InputLine title="Attendance Adjustments">
                                  <div className="w-full py-2">
                                    <AttendanceInputContext.Provider
                                      value={{
                                        id,
                                        generalForm: attendanceForm,
                                        setSuccessfullyUpdated: () => {},
                                        fullscreen,
                                        setFullscreen,
                                        successfullyUpdated: true,
                                        handleCloseForm,
                                        handleInactivate: () => null,
                                        canceled: false,
                                      }}
                                    >
                                      <AttendanceTable
                                        attendances={pageData.attendances}
                                        Ctx={AttendanceInputContext}
                                        formRef={attendanceForm}
                                        readOnly={true}
                                      />
                                    </AttendanceInputContext.Provider>
                                  </div>
                                </InputLine>
                              )}

                              <InputLine title="Rollback">
                                <div className="py-3">
                                  <button
                                    type="button"
                                    onClick={handleRollback}
                                    disabled={submitting}
                                    className="flex flex-row items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs rounded-md px-4 py-2 h-8 disabled:opacity-50"
                                  >
                                    <RotateCcw size={14} /> Revert Approval
                                  </button>
                                  <p className="text-xs text-gray-400 mt-1">Marks all linked attendances as Absent and returns this request to the Academic queue as Pending.</p>
                                </div>
                              </InputLine>
                            </>
                          )}

                          {pageData.status === "Denied" && (
                            <>
                              <InputLine title="Status">
                                <div className="py-3">
                                  <StatusBadge status="Denied" />
                                </div>
                              </InputLine>
                              {pageData.denied_reason && (
                                <InputLine title="Denied Reason">
                                  <Textarea
                                    name="_denied_reason"
                                    title="Denied Reason"
                                    grow
                                    readOnly
                                    defaultValue={pageData.denied_reason}
                                    InputContext={InputContext}
                                    rows={4}
                                  />
                                </InputLine>
                              )}
                            </>
                          )}
                        </InputLineGroup>
                      )}


                      {!isNew && (
                        <InputLineGroup title="timeline" activeMenu={activeMenu === "timeline"}>
                          <div className="p-4 w-full">
                            {pageData.logs && pageData.logs.length > 0 ? (
                              pageData.logs.map((log) => (
                                <TimelineEntry key={log.id} log={log} />
                              ))
                            ) : (
                              <p className="text-xs text-gray-400">No activity yet.</p>
                            )}
                          </div>
                        </InputLineGroup>
                      )}

                      {!isNew && (
                        <InputLineGroup title="internal_notes" activeMenu={activeMenu === "internal_notes"}>
                          <div className="w-full px-4 py-2">
                            <div className="flex items-center gap-2 mb-3">
                              <MessageSquare size={16} className="text-mila_orange" />
                              <span className="text-sm font-semibold text-gray-700">Internal Notes</span>
                              <span className="text-xs text-gray-400 ml-1">— visible to staff only, not sent to the student</span>
                            </div>
                          </div>
                          <Comments medical_excuse_id={id} />
                        </InputLineGroup>
                      )}
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </Form>
              </div>
            </InputContext.Provider>
          </div>
        </div>
      ) : (
        <FormLoading />
      )}
    </Preview>
  );
}
